import {getDsContext} from "@/utils/dsContext";
import {useScoutingVisions, visionData} from "@/utils/scoutingVisionData";
import {ScoutingVisionsAPI} from "@/api/scouting_visions";
import VLFSelectNew from "@/components/Input/SelectNew";
import {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {Link} from "react-router-dom";
import {Button, ButtonGroup, FormControlLabel, Switch} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import RateReviewIcon from "@mui/icons-material/RateReview";
import MainActions from "@/components/MainActions";
import VLFTextField from "@/components/Input/TextField";
import RostersSection, {
	VisionControls,
} from "@/pages/Scouting/Matches/Match/RostersSection";
import HandlePlayerModal from "@/pages/Scouting/Matches/Match/handlePlayer";
import {MatchAPI} from "@/api/match";
import {PlayerAPI} from "@/api/player";
import {getPlayerDetailPath} from "@/utils/functions";
import {VISION_MODES, VISION_VIEWS, isEmptyRow} from "@/utils/scoutingVisions";
import {reportMatch} from "@/utils/scoutingReports";
import {useNavigate} from "react-router-dom";
import moment from "moment";
import "./index.scss";

const targetMeta = (player) =>
	[
		player.goalkeeper ? "Portiere" : player.secondaryRole || player.primaryRole,
		player.dateOfBirth ? moment(player.dateOfBirth).year() : null,
	]
		.filter(Boolean)
		.join(" · ");

const annotatedCount = (rows) =>
	Object.values(rows || {}).filter((row) => !isEmptyRow(row)).length;

function MatchScoutingEditor({matchData, onRefresh, handleNotification}) {
	const navigate = useNavigate();
	const user = useSelector((state) => state?.user);
	const seasonId = useSelector((state) => state?.season?.id);
	const observer = user?.userId
		? {
				id: user.userId,
				name:
					[user.firstName, user.lastName].filter(Boolean).join(" ") ||
					user.userName,
			}
		: null;
	const visionState = useScoutingVisions({
		matchId: matchData.id,
		enabled: !!matchData.canReadScouting,
	});
	const {vision, pending, setMode, setRow} = visionState;
	const [targetEditor, setTargetEditor] = useState(null);
	const canCreateReport =
		user?.roleName?.toLowerCase() === "admin" ||
		user?.areaPermissions?.some(
			(p) => p.area === "Scouting" && p.accessLevel === "FullControl",
		);
	const [query, setQuery] = useState(""),
		[view, setView] = useState("roster");
	const otherVisions = visionState.matchVisions.filter(
		(v) => v.observer?.id !== observer?.id && annotatedCount(v.rows),
	);
	const [assigned, setAssigned] = useState([]);
	const [observers, setObservers] = useState([]);
	const [observersError, setObserversError] = useState(false);
	useEffect(() => {
		if (!matchData.canManageScouting) return;
		let active = true;
		ScoutingVisionsAPI.observers(matchData.id)
			.then((rows) => {
				if (active) {
					setObservers(rows);
					setObserversError(false);
				}
			})
			.catch(() => {
				if (active) setObserversError(true);
			});
		return () => {
			active = false;
		};
	}, [matchData.id, matchData.canManageScouting]);
	const [planned, setPlanned] = useState(false),
		[names, setNames] = useState("");
	const [notes, setNotes] = useState("");
	const [busy, setBusy] = useState(false),
		[openSide, setOpenSide] = useState(null);
	const [rosterIds, setRosterIds] = useState({home: null, away: null});
	useEffect(() => {
		setPlanned(!!matchData.scoutingPlanned);
		setAssigned(matchData.scoutUserIds || []);
		setNames(matchData.scoutNames || "");
		setNotes(matchData.notes || "");
	}, [
		matchData.id,
		matchData.scoutingPlanned,
		matchData.scoutNames,
		matchData.notes,
		matchData.scoutUserIds,
	]);
	const canEdit = matchData.canManageScouting;
	const dirty =
		JSON.stringify([...assigned].sort()) !==
			JSON.stringify([...(matchData.scoutUserIds || [])].sort()) ||
		planned !== !!matchData.scoutingPlanned ||
		names !== (matchData.scoutNames || "") ||
		notes !== (matchData.notes || "");
	const run = async (action) => {
		setBusy(true);
		try {
			await action();
			await onRefresh();
		} catch (e) {
			handleNotification(
				e.response?.data?.error || "Operazione non riuscita",
				"error",
			);
			throw e;
		} finally {
			setBusy(false);
		}
	};
	const observe = async (playerId, side) => {
		await run(() =>
			MatchAPI.createObservedPlayer({
				matchId: matchData.id,
				playerId,
				matchSide: side,
			}),
		);
		setOpenSide(null);
	};
	const targets = matchData.prospectsObserved || [];

	const sides = [
		{side: "home", label: "Casa", club: matchData.homeClub},
		{side: "away", label: "Trasferta", club: matchData.awayClub},
	];
	// Lato di ogni obiettivo: rosa caricata o distinta, poi lato scelto all'aggiunta.
	const targetsBySide = useMemo(() => {
		const sideOf = (playerId) => {
			const stored = targets.find((t) => t.playerId === playerId)?.matchSide;
			if (stored === "home" || stored === "away") return stored;
			for (const {side} of sides) {
				if (
					rosterIds[side]?.includes(playerId) ||
					(matchData.matchPlayers || []).some(
						(p) => p.playerId === playerId && p.matchSide === side,
					)
				)
					return side;
			}
			return "other";
		};
		const groups = {home: [], away: [], other: []};
		targets.forEach((t) => groups[sideOf(t.playerId)].push(t));
		return groups;
	}, [targets, rosterIds, matchData.matchPlayers]); // eslint-disable-line react-hooks/exhaustive-deps

	const rostersReady = !!rosterIds.home && !!rosterIds.away;

	const openPlayer = async (playerId) => {
		try {
			navigate(
				getPlayerDetailPath(await PlayerAPI.getById(playerId, {seasonId})),
			);
		} catch {
			handleNotification("Impossibile aprire il giocatore", "error");
		}
	};

	const renderTarget = (target) => {
		const player = target.playerData || {};
		const side =
			target.matchSide ||
			(targetsBySide.home.includes(target)
				? "home"
				: targetsBySide.away.includes(target)
					? "away"
					: null);
		const name =
			[player.firstName, player.lastName].filter(Boolean).join(" ") ||
			"Giocatore";
		return (
			<div key={target.playerId}>
				<div className="match-scouting__target">
					<button
						className="match-scouting__name"
						type="button"
						title="Apri scheda giocatore"
						aria-label={`Apri scheda ${name}`}
						onClick={() => openPlayer(target.playerId)}
					>
						<strong>{name}</strong>
						<small>{targetMeta(player)}</small>
					</button>
					{canCreateReport && (
						<Link
							title="Nuova relazione"
							aria-label={`Nuova relazione ${name}`}
							to={`/scouting/relazioni/new?playerId=${target.playerId}`}
							state={{match: reportMatch(matchData)}}
							onClick={async (event) => {
								event.preventDefault();
								try {
									await visionState.flush();
									const saved =
										visionData().matches[matchData.id]?.[observer?.id];
									navigate(
										`/scouting/relazioni/new?playerId=${target.playerId}`,
										{
											state: {
												match: reportMatch(matchData),
												visionId: saved?.rows?.[target.playerId]
													? saved.id
													: null,
											},
										},
									);
								} catch {
									handleNotification(
										"Salva prima la visione e riprova",
										"error",
									);
								}
							}}
						>
							<RateReviewIcon fontSize="small" />
						</Link>
					)}
					<MainActions
						actions={[
							{
								label: "Annota",
								title: `Annota obiettivo ${name}`,
								disabled: !visionState.canWrite || !side,
								onClick: () =>
									setTargetEditor(
										targetEditor === target.playerId ? null : target.playerId,
									),
							},
						]}
					/>
					{canEdit && (
						<Button
							disabled={busy}
							title="Rimuovi obiettivo"
							aria-label={`Rimuovi obiettivo ${name}`}
							onClick={() =>
								run(() =>
									MatchAPI.deleteObservedPlayer(matchData.id, target.playerId),
								).catch(() => {})
							}
						>
							<CloseIcon fontSize="small" />
						</Button>
					)}
				</div>
				{targetEditor === target.playerId && side && (
					<VisionControls
						row={vision.rows[target.playerId]}
						name={name}
						onChange={(patch) => setRow(target.playerId, side, patch)}
					/>
				)}
			</div>
		);
	};

	const openTeam = sides.find((s) => s.side === openSide);

	return (
		<div className="match-scouting">
			{visionState.readError && (
				<div role="alert" className="alert alert-warning">
					Visioni non disponibili.{" "}
					<MainActions
						actions={[
							{
								label: "Riprova",
								onClick: () => visionState.refresh().catch(() => {}),
							},
						]}
					/>
				</div>
			)}
			{visionState.saveError && (
				<div role="alert" className="alert alert-warning">
					Le modifiche alla visione non sono ancora salvate.{" "}
					<MainActions
						actions={[
							{
								label: "Riprova salvataggio",
								onClick: () => visionState.flush().catch(() => {}),
							},
						]}
					/>
				</div>
			)}
			<section className="match-scouting__card">
				<header className="match-scouting__head">
					<h3>Pianificazione</h3>
					<FormControlLabel
						className="match-scouting__switch"
						control={
							<Switch
								checked={planned}
								disabled={!canEdit || busy}
								onChange={(e) => setPlanned(e.target.checked)}
							/>
						}
						label="Da osservare"
					/>
				</header>
				<div className="match-scouting__fields">
					<div>
						<VLFSelectNew
							label="Osservatori assegnati"
							isMulti
							options={observers.map((u) => ({value: u.id, label: u.name}))}
							value={assigned.map((id) => ({
								value: id,
								label:
									observers.find((u) => u.id === id)?.name ||
									matchData.assignedScouts?.find((u) => u.id === id)?.name ||
									"Osservatore",
							}))}
							onChange={(options) =>
								setAssigned((options || []).map((o) => o.value))
							}
							isDisabled={!canEdit || busy || observersError}
						/>
						{observersError && (
							<p role="alert">
								Elenco osservatori non disponibile. Riapri la gara per
								riprovare.
							</p>
						)}
						<p>
							{assigned.length} assegnati ·{" "}
							{visionState.loading || visionState.readError
								? "stato visioni non disponibile"
								: `${visionState.matchVisions.filter((v) => annotatedCount(v.rows)).length} visioni con annotazioni`}
						</p>
						{(matchData.assignedScouts || []).map((scout) => (
							<div key={scout.id}>
								{scout.name}:{" "}
								{visionState.matchVisions.some(
									(v) => v.observer.id === scout.id && annotatedCount(v.rows),
								)
									? "visione presente"
									: visionState.loading || visionState.readError
										? "stato non disponibile"
										: "in attesa"}
							</div>
						))}
					</div>
					<div>
						<VLFTextField
							size="small"
							label="Altri osservatori (testo libero)"
							placeholder="Es. Rossi, Bianchi"
							value={names}
							disabled={!canEdit || busy}
							inputProps={{maxLength: 255, "aria-label": "Osservatori"}}
							onChange={(e) => setNames(e.target.value)}
						/>
					</div>
					<div>
						<VLFTextField
							label="Note della partita"
							placeholder="Note sulla gara"
							value={notes}
							onChange={(event) => setNotes(event.target.value)}
							multiline
							minRows={2}
							maxRows={10}
							size="small"
							disabled={!canEdit || busy}
							inputProps={{
								maxLength: 10000,
								"aria-label": "Note della partita",
							}}
						/>
					</div>
				</div>
				{canEdit && (
					<footer className="match-scouting__foot">
						{dirty && <span className="is-dirty">Modifiche non salvate</span>}
						<MainActions
							actions={[
								{
									icon: <SaveIcon />,
									label: "Salva",
									title: "Salva",
									disabled: busy || !dirty,
									onClick: () =>
										run(() =>
											MatchAPI.saveScouting(matchData.id, {
												planned,
												scoutNames: names,
												scoutUserIds: assigned,
												notes,
											}),
										).catch(() => {}),
								},
							]}
						/>
					</footer>
				)}
			</section>

			<section className="match-scouting__card">
				<header className="match-scouting__head">
					<div>
						<h3>
							Obiettivi scouting
							{targets.length > 0 && (
								<span className="match-scouting__count">{targets.length}</span>
							)}
						</h3>
						<p>Tocca l'occhio sulle rose qui sotto o aggiungi un giocatore.</p>
					</div>
				</header>
				<div className="match-scouting__sides">
					{sides.map(({side, label, club}) => (
						<div key={side} className="match-scouting__side">
							<div className="match-scouting__side-head">
								<span>
									<small>{label}</small>
									<strong>{club || "Squadra da inserire"}</strong>
								</span>
								{canEdit && (
									<MainActions
										actions={[
											{
												icon: <AddIcon />,
												label: "Aggiungi",
												title: `Aggiungi obiettivo ${label}`,
												disabled: busy,
												onClick: () => setOpenSide(side),
											},
										]}
									/>
								)}
							</div>
							{!rostersReady ? (
								<p className="match-scouting__empty">Caricamento…</p>
							) : targetsBySide[side].length ? (
								targetsBySide[side].map(renderTarget)
							) : (
								<p className="match-scouting__empty">Nessun obiettivo</p>
							)}
						</div>
					))}
					{rostersReady && targetsBySide.other.length > 0 && (
						<div className="match-scouting__side">
							<div className="match-scouting__side-head">
								<span>
									<small>Senza squadra</small>
									<strong>Non presenti nelle rose delle due squadre</strong>
								</span>
							</div>
							{targetsBySide.other.map(renderTarget)}
						</div>
					)}
				</div>
			</section>

			<section className="match-scouting__card">
				<header className="match-scouting__head">
					<div>
						<h3>
							La mia visione
							{annotatedCount(vision.rows) > 0 && (
								<span className="match-scouting__count">
									{annotatedCount(vision.rows)}
								</span>
							)}
						</h3>
						<p>
							{observer?.name ? observer.name + " · " : ""}
							Tocca la matita su un giocatore per annotarlo. Si salva da sola.
						</p>
					</div>
					<ButtonGroup
						disableElevation
						size="small"
						aria-label="Modalità della visione"
					>
						{VISION_MODES.map((mode) => (
							<Button
								key={mode.id}
								variant={vision.mode === mode.id ? "contained" : "outlined"}
								aria-pressed={vision.mode === mode.id}
								disabled={!observer || !visionState.canWrite}
								onClick={() => setMode(mode.id)}
							>
								{mode.label}
							</Button>
						))}
					</ButtonGroup>
				</header>
				<div className="match-scouting__toolbar">
					<div className="match-scouting__search">
						<VLFTextField
							size="small"
							placeholder="Cerca giocatore"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							inputProps={{"aria-label": "Cerca giocatore"}}
						/>
					</div>
					<ButtonGroup
						disableElevation
						size="small"
						aria-label="Giocatori da mostrare"
					>
						{VISION_VIEWS.map((option) => (
							<Button
								key={option.id}
								variant={view === option.id ? "contained" : "outlined"}
								aria-pressed={view === option.id}
								onClick={() => setView(option.id)}
							>
								{option.label}
							</Button>
						))}
					</ButtonGroup>
					<span
						className={"match-scouting__saved" + (pending ? " is-dirty" : "")}
						role="status"
					>
						{visionState.saveError
							? "Salvataggio non riuscito"
							: visionState.loading
								? "Caricamento…"
								: pending
									? "Salvataggio…"
									: vision.updatedAt
										? "Salvata alle " + moment(vision.updatedAt).format("HH:mm")
										: "Nessuna annotazione"}
					</span>
				</div>
				{otherVisions.length > 0 && (
					<ul className="match-scouting__others" aria-label="Altre visioni">
						{otherVisions.map((v) => (
							<li key={v.observer.id}>
								<strong>{v.observer.name}</strong>
								{" · "}
								{VISION_MODES.find((m) => m.id === v.mode)?.label}
								{" · "}
								{annotatedCount(v.rows)} annotati
								{" · "}
								{moment(v.updatedAt).format("DD/MM HH:mm")}
								<ul>
									{Object.values(v.rows).map((row) => (
										<li key={row.playerId}>
											<button
												type="button"
												className="match-scouting__name"
												onClick={() => openPlayer(row.playerId)}
											>
												{[row.player?.firstName, row.player?.lastName]
													.filter(Boolean)
													.join(" ") || "Giocatore"}
											</button>
											{row.starter === true
												? " · Titolare"
												: row.starter === false
													? " · Riserva"
													: ""}{" "}
											· {(row.attitudes || []).join(", ")} {row.note}
										</li>
									))}
								</ul>
							</li>
						))}
					</ul>
				)}
			</section>

			<h3>Giocatori delle squadre</h3>
			<RostersSection
				matchData={matchData}
				onObservePlayer={canEdit && !busy ? observe : undefined}
				onOpenPlayer={openPlayer}
				onRosterLoaded={(side, roster) =>
					setRosterIds((prev) => ({
						...prev,
						[side]: (roster?.players || []).map((p) => p.playerId),
					}))
				}
				targetIds={targets.map((t) => t.playerId)}
				visionRows={vision.rows}
				onVisionChange={observer && visionState.canWrite ? setRow : undefined}
				query={query}
				view={view}
			/>
			{openTeam && (
				<HandlePlayerModal
					open
					includeInternal
					title={
						"Aggiungi obiettivo · " +
						(openTeam.club || openTeam.label) +
						" (" +
						openTeam.label.toLowerCase() +
						")"
					}
					confirm={(data) => observe(data.playerId, openSide).catch(() => {})}
					close={() => setOpenSide(null)}
				/>
			)}
		</div>
	);
}

export default function MatchScouting(props) {
	useSelector((state) => state.user);
	useSelector((state) => state.token);
	useSelector((state) => state.season?.id);
	return (
		<MatchScoutingEditor
			key={getDsContext() + ":" + props.matchData.id}
			{...props}
		/>
	);
}
